import { useState, useRef, useEffect } from 'react'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'

export function ClientImport({ onImport, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [columnMap, setColumnMap] = useState({})
  const [replaceAll, setReplaceAll] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [progress, setProgress] = useState(0)   // 0–100
  const timerRef = useRef(null)

  const startTimer = () => {
    setElapsed(0)
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000)
  }

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => () => stopTimer(), [])

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`
  }

  const REQUIRED_COLUMNS = {
    // Customer code aliases
    'clientcode':     'customer_code',
    'customercode':   'customer_code',
    'customer code':  'customer_code',
    'client code':    'customer_code',
    // Name aliases
    'name':           'first_name',
    'firstname':      'first_name',
    'first name':     'first_name',
    'full name':      'full_name',
    'fullname':       'full_name',
    // Entity
    'entity name':    'entity_name',
    'entityname':     'entity_name',
    // Surname
    'surname':        'surname',
    'lastname':       'surname',
    'last name':      'surname',
    // Contact
    'email':          'email_address',
    'email address':  'email_address',
    'emailaddress':   'email_address',
    'phone':          'mobile_number',
    'phone number':   'mobile_number',
    'phonenumber':    'mobile_number',
    'mobile':         'mobile_number',
    'mobile number':  'mobile_number',
    'mobilenumber':   'mobile_number',
    // Date of birth
    'birthday':       'date_of_birth',
    'dateofbirth':    'date_of_birth',
    'date of birth':  'date_of_birth',
    'dob':            'date_of_birth',
    // Other
    'id number':      'id_number',
    'idnumber':       'id_number',
    'status':         'client_status',
    'client status':  'client_status',
    'active':         'active',
  }

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setError('')
    setFile(selectedFile)
    setLoading(true)

    try {
      // Dynamically import XLSX
      const XLSX = await import('xlsx')
      const reader = new FileReader()

      reader.onload = (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          if (rows.length === 0) {
            setError('No data found in file')
            return
          }

          // Get headers from first row
          const headers = Object.keys(rows[0])

          // Auto-map columns — normalise to lowercase with spaces preserved
          const newMap = {}
          headers.forEach(header => {
            const normalised = header.toLowerCase().trim()
            // Exact match first (handles 'entity name', 'phone number', etc.)
            if (REQUIRED_COLUMNS[normalised] !== undefined) {
              newMap[header] = REQUIRED_COLUMNS[normalised]
            } else {
              // Strip all spaces and try again (handles 'ClientCode', 'EmailAddress', etc.)
              const stripped = normalised.replace(/\s+/g, '')
              if (REQUIRED_COLUMNS[stripped] !== undefined) {
                newMap[header] = REQUIRED_COLUMNS[stripped]
              }
            }
          })
          setColumnMap(newMap)

          // Show preview (first 5 rows)
          setTotalRows(rows.length)
          setPreview(rows.slice(0, 5))
        } catch (err) {
          setError(`Error reading file: ${err.message}`)
        }
      }

      reader.readAsBinaryString(selectedFile)
    } catch (err) {
      setError(`Error loading Excel library: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleColumnMapChange = (excelColumn, dbColumn) => {
    setColumnMap(prev => ({
      ...prev,
      [excelColumn]: dbColumn
    }))
  }

  const handleImport = async () => {
    if (!file || !preview) {
      setError('No file selected')
      return
    }

    setError('')
    setLoading(true)
    startTimer()

    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()

      reader.onload = async (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          // Map columns based on columnMap
          const mappedData = rows.map(row => {
            const mapped = {}
            Object.entries(columnMap).forEach(([excelCol, dbCol]) => {
              if (dbCol && row[excelCol] !== undefined) {
                // Handle boolean columns
                if (dbCol === 'active') {
                  mapped[dbCol] = row[excelCol] === true || row[excelCol] === 'TRUE' || row[excelCol] === 'Yes'
                } else {
                  // Coerce to string — Excel reads numeric codes (e.g. 900100) as numbers
                  mapped[dbCol] = String(row[excelCol]).trim()
                }
              }
            })
            return mapped
          })

          // Normalise each row: build full_name, handle entity rows, convert dates
          const normalizedData = mappedData
            .filter(row => Object.keys(row).length > 0)
            .map(row => {
              // 1. Build full_name from parts
              if (!row.full_name) {
                if (row.first_name && row.surname)  row.full_name = `${row.first_name} ${row.surname}`
                else if (row.first_name)            row.full_name = row.first_name
                else if (row.entity_name)           row.full_name = row.entity_name
                else if (row.surname)               row.full_name = row.surname
              }
              // 2. Surname fallback
              if (!row.surname) row.surname = row.entity_name || row.full_name || 'N/A'
              // 3. Excel serial date → ISO string
              if (row.date_of_birth && typeof row.date_of_birth === 'number') {
                row.date_of_birth = new Date(Math.round((row.date_of_birth - 25569) * 86400000))
                  .toISOString().split('T')[0]
              }
              // 4. Active flag
              row.active = row.active !== false && row.active !== 'FALSE' && row.active !== 'No'
              // 5. Remove temp fields not in DB schema
              delete row.first_name
              delete row.entity_name
              return row
            })
            .filter(row => row.customer_code) // skip rows without a client code

          if (normalizedData.length === 0) {
            setError('No valid data to import. Ensure Customer Code and at least a name column are mapped.')
            setLoading(false)
            return
          }

          setProgress(0)
          const timeout = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Import timed out. Your session may have expired — please log out and log back in, then try again.')), 90000)
          )
          await Promise.race([
            onImport(normalizedData, { replaceAll, onProgress: (done, total) => setProgress(Math.round((done / total) * 100)) }),
            timeout,
          ])
          stopTimer()
          setLoading(false)
        } catch (err) {
          console.error('Import error:', err)
          stopTimer()
          const msg = err.message || ''
          const displayMsg = (msg.toLowerCase().includes('refresh token') || msg.toLowerCase().includes('jwt') || msg.toLowerCase().includes('session'))
            ? 'Your session has expired. Please log out and log back in, then try the import again.'
            : `Something went wrong during import. Please try again later.\n(${msg})`
          setError(displayMsg)
          setLoading(false)
        }
      }

      reader.onerror = () => {
        stopTimer()
        setError('Could not read the file. Please try again later.')
        setLoading(false)
      }

      reader.readAsBinaryString(file)
    } catch (err) {
      stopTimer()
      setError(`Something went wrong. Please try again later.\n(${err.message})`)
      setLoading(false)
    }
  }

  const excelColumns = preview ? Object.keys(preview[0]) : []

  return (
    <Modal isOpen={true} onClose={onClose} title="Import Clients" sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode}>
      <div className="space-y-4 max-h-[80vh] flex flex-col">
        {error && (
          <div className="px-4 py-3 rounded text-sm space-y-1" style={{ backgroundColor: isDarkMode ? '#7f1d1d' : '#fef2f2', borderColor: isDarkMode ? '#991b1b' : '#fecaca', color: isDarkMode ? '#fca5a5' : '#b91c1c' , borderWidth: '1px' }}>
            {error.split('\n').map((line, i) => (
              <p key={i} className={i === 1 ? 'text-xs font-mono' : ''} style={{ color: isDarkMode ? '#fca5a5' : '#b91c1c' }}>{line}</p>
            ))}
            <button
              className="mt-1 text-xs underline"
              style={{ color: isDarkMode ? '#fca5a5' : '#b91c1c' }}
              onMouseEnter={(e) => e.target.style.color = isDarkMode ? '#fecaca' : '#991b1b'}
              onMouseLeave={(e) => e.target.style.color = isDarkMode ? '#fca5a5' : '#b91c1c'}
              onClick={() => setError('')}
            >
              Dismiss and try again
            </button>
          </div>
        )}

        {!preview ? (
          // File Upload Section
          <div className="border-2 border-dashed rounded-lg p-8 text-center" style={{ borderColor: isDarkMode ? '#475569' : '#d1d5db', backgroundColor: isDarkMode ? '#1e293b' : '#f9fafb' }}>
            <input
              type="file"
              id="file-input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
            <label
              htmlFor="file-input"
              className="cursor-pointer"
            >
              <p className="text-lg font-semibold mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                📁 Drop file here or click to select
              </p>
              <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                Supported: Excel (.xlsx, .xls) or CSV
              </p>
              {loading && <p className="text-sm text-sfp-orange mt-2">Loading...</p>}
            </label>
          </div>
        ) : (
          // Column Mapping & Preview Section
          <div className="space-y-4 flex-1 overflow-y-auto">

            {/* Replace All toggle */}
            <label style={{ borderColor: isDarkMode ? (replaceAll ? '#7f1d1d' : '#475569') : (replaceAll ? '#fca5a5' : '#e5e7eb'), backgroundColor: isDarkMode ? (replaceAll ? '#1f2937' : '#1e293b') : (replaceAll ? '#fee2e2' : '#f9fafb') }} className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer">
              <input
                type="checkbox"
                checked={replaceAll}
                onChange={e => setReplaceAll(e.target.checked)}
                className="mt-0.5 accent-red-500"
              />
              <div>
                <p className="text-sm font-medium" style={{ color: replaceAll ? '#dc2626' : (isDarkMode ? '#e2e8f0' : '#333333') }}>
                  Replace All (upsert + remove no-code clients)
                </p>
                {replaceAll && (
                  <p className="text-xs text-red-500 mt-0.5">
                    ⚠️ Existing clients <strong>without</strong> a Customer Code will be deleted.
                    All others will be updated or added by Customer Code.
                  </p>
                )}
              </div>
            </label>

            <div>
              <h3 className="font-semibold text-sm mb-3" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                Map Excel Columns to Database Fields
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {excelColumns.map(col => (
                  <div key={col} className="flex gap-2 items-center text-sm">
                    <span className="flex-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>{col}</span>
                    <span style={{ color: isDarkMode ? '#cbd5e1' : '#999999' }}>→</span>
                    <select
                      value={columnMap[col] || ''}
                      onChange={(e) => handleColumnMapChange(col, e.target.value)}
                      className="input-field flex-1 text-sm py-1"
                    >
                      <option value="">Skip</option>
                      <option value="customer_code">Customer Code</option>
                      <option value="first_name">First Name</option>
                      <option value="full_name">Full Name</option>
                      <option value="surname">Surname</option>
                      <option value="entity_name">Entity Name</option>
                      <option value="mobile_number">Mobile Number</option>
                      <option value="email_address">Email Address</option>
                      <option value="date_of_birth">Date of Birth</option>
                      <option value="id_number">ID Number</option>
                      <option value="tax_number">Tax Number</option>
                      <option value="client_status">Status</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                Preview (first 5 rows)
              </h3>
              <div style={{ borderColor: isDarkMode ? '#475569' : '#e5e7eb' }} className="overflow-x-auto border rounded max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="table-header sticky top-0">
                    <tr>
                      {excelColumns.map(col => (
                        <th key={col} className="px-3 py-2 text-left">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        {excelColumns.map(col => (
                          <td key={col} className="px-3 py-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                            {String(row[col] || '').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-2" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                Showing 5 of rows in preview
              </p>
            </div>
          </div>
        )}

        {/* Import progress overlay */}
        {loading && preview && (
          <div className="bg-sfp-orange/5 border border-sfp-orange/30 rounded-lg px-4 py-3 space-y-2">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-4 h-4 border-2 border-sfp-orange border-t-transparent rounded-full animate-spin" />
              <p className="flex-1 text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>
                {progress > 0
                  ? `Importing… ${progress}% (${Math.round(totalRows * progress / 100).toLocaleString()} / ${totalRows.toLocaleString()} records)`
                  : `Preparing ${totalRows.toLocaleString()} records…`}
              </p>
              <span className="flex-shrink-0 text-sm font-mono font-semibold text-sfp-orange tabular-nums">
                {formatElapsed(elapsed)}
              </span>
            </div>
            {/* Progress bar */}
            <div style={{ backgroundColor: isDarkMode ? '#334155' : '#e5e7eb' }} className="w-full rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-sfp-orange h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.max(progress, 3)}%` }}
              />
            </div>
            <p className="text-xs" style={{ color: isDarkMode ? '#cbd5e1' : '#999999' }}>Please do not close this window.</p>
          </div>
        )}

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${isDarkMode ? '#475569' : '#e5e7eb'}` }} className="flex gap-2 justify-end pt-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          {preview && (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setPreview(null)
                  setFile(null)
                  setColumnMap({})
                }}
                type="button"
                disabled={loading}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={loading}
              >
                {loading ? 'Importing…' : `Import ${totalRows.toLocaleString()} Records`}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
